/**
 * @file The call recorder (#2223 Phase 5, J1): what happened to one person in
 * a call -- joined, mic/camera, how they left, network and path changes,
 * recovery attempts, levels -- sent to the super admin's call timeline.
 *
 * Design context:
 * - Runs only while the server says `livekit.call_diagnostics` (the
 *   environment switch, read through `useCallDiagnosticsStore`). Off = no
 *   listeners, no timers, no requests.
 * - Two layers. The *session* lives at page level, one per call: the queue,
 *   the 10 s flush, `pagehide`/Navigation API listeners and the
 *   `emitCallActivity` bus. The *hook* attaches one LiveKit `Room` to it. The
 *   guest call remounts `<LiveKitRoom>` (a new Room) on every reconnect, so
 *   the session survives a short unmount (RELEASE_GRACE_MS) and the exit
 *   listeners and recovery events are never lost in between. The signed-in
 *   call keeps one Room across rejoins; a new participation is a new
 *   participant sid, and the queue splits batches by sid.
 * - A Leave button calls `recordCallLeft()`: it queues `left_button`, sends it
 *   with keepalive (the page may navigate) and ends the session, so a later
 *   tab close is not a second goodbye.
 * - Never records audio, video, chat, transcripts or typed text (AC40): only
 *   flags, levels (numbers), states and the public IP.
 * - Nothing is put on the call's AudioContext or RoomOptions (a changed
 *   options object makes <LiveKitRoom> rebuild the Room); everything reads
 *   the Room through its public API.
 */
import { useEffect, useRef } from "react";
import { ConnectionState, RoomEvent, Track, type Room, type LocalTrack, type RemoteTrack } from "livekit-client";
import { videoService } from "../api/video.api";
import { useCallDiagnosticsStore } from "../store/callDiagnosticsStore";
import { useVoicePathStore } from "../store/voicePathStore";
import { describeBrowser } from "../lib/callQuality";
import {
  CallActivityQueue,
  FLUSH_INTERVAL_MS,
  HIDDEN_BODY_BUDGET,
  PAGEHIDE_BODY_BUDGET,
  browserFacts,
  errorData,
  exitKind,
  hasRemoteInbound,
  hasSrflx,
  inboundAudioLevel,
  isCrossDocumentNavigate,
  networkType,
  onCallActivity,
  pathData,
  pathFactsFrom,
  pathKey,
  type CallActivityData,
  type CallActivityKind,
} from "../lib/callActivity";

/** A remount within this time (guest reconnect) keeps the same session. */
export const RELEASE_GRACE_MS = 1500;
const LEVELS_MS = 1000;
const PATH_MS = 3000;

function newClientId(): string {
  const c = globalThis.crypto as Crypto | undefined;
  if (c?.randomUUID) return c.randomUUID();
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (ch) => {
    const r = (Math.random() * 16) | 0;
    return (ch === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
}

/** One per page load (contract: `client_id`). */
export const CLIENT_ID = newClientId();

interface Session {
  roomId: string;
  token: string;
  queue: CallActivityQueue;
  /** The participant sid now in use; set by the attached Room. */
  sid: () => string | null;
  record: (kind: CallActivityKind, data?: CallActivityData) => void;
  refs: number;
  releaseTimer: ReturnType<typeof setTimeout> | null;
  end: () => void;
}

let session: Session | null = null;

const diagnosticsOn = () => useCallDiagnosticsStore.getState().enabled;

function createSession(roomId: string, token: string): Session {
  let lastNavigateAt: number | null = null;
  const s: Session = {
    roomId,
    token,
    queue: new CallActivityQueue(
      CLIENT_ID,
      (body, { keepalive }) => videoService.reportCallActivity(s.roomId, s.token, body, { keepalive }),
      diagnosticsOn,
    ),
    sid: () => null,
    record: (kind, data = {}) => s.queue.push(kind, data, s.sid()),
    refs: 0,
    releaseTimer: null,
    end: () => {},
  };

  const flushTimer = setInterval(() => void s.queue.flush(), FLUSH_INTERVAL_MS);
  const offBus = onCallActivity((kind, data) => s.record(kind, data));

  const onPageHide = (e: PageTransitionEvent) => {
    s.record(exitKind(lastNavigateAt, Date.now()));
    void s.queue.flush({ keepalive: true, budget: PAGEHIDE_BODY_BUDGET });
    // Into the back/forward cache: the page may come back, keep listening.
    if (!e.persisted) s.end();
  };
  const onVisibility = () => {
    if (document.visibilityState === "hidden") void s.queue.flush({ keepalive: true, budget: HIDDEN_BODY_BUDGET });
  };
  const navigation = (window as Window & { navigation?: EventTarget }).navigation;
  const onNavigate = (e: Event) => {
    if (isCrossDocumentNavigate(e)) lastNavigateAt = Date.now();
  };
  const onNavigateError = () => {
    lastNavigateAt = null;
  };
  window.addEventListener("pagehide", onPageHide);
  document.addEventListener("visibilitychange", onVisibility);
  navigation?.addEventListener?.("navigate", onNavigate);
  navigation?.addEventListener?.("navigateerror", onNavigateError);

  let ended = false;
  s.end = () => {
    if (ended) return;
    ended = true;
    if (s.releaseTimer) clearTimeout(s.releaseTimer);
    clearInterval(flushTimer);
    offBus();
    window.removeEventListener("pagehide", onPageHide);
    document.removeEventListener("visibilitychange", onVisibility);
    navigation?.removeEventListener?.("navigate", onNavigate);
    navigation?.removeEventListener?.("navigateerror", onNavigateError);
    s.queue.end();
    void s.queue.flush({ keepalive: true, budget: PAGEHIDE_BODY_BUDGET });
    if (session === s) session = null;
  };
  return s;
}

function acquire(roomId: string, token: string): Session {
  if (session && session.roomId !== roomId) session.end();
  session ??= createSession(roomId, token);
  const s = session;
  s.token = token;
  s.refs += 1;
  if (s.releaseTimer) {
    clearTimeout(s.releaseTimer);
    s.releaseTimer = null;
  }
  return s;
}

function release(s: Session): void {
  s.refs -= 1;
  if (s.refs > 0) return;
  s.releaseTimer = setTimeout(() => s.end(), RELEASE_GRACE_MS);
}

/**
 * The user pressed Leave (any Leave button, signed-in or guest). Sends
 * `left_button` at once and ends the recording for this call.
 */
export function recordCallLeft(): void {
  const s = session;
  if (!s) return;
  s.record("left_button");
  s.end();
}

/** Test seam: drop the page-level session. */
export function __resetCallActivitySession(): void {
  session?.end();
  session = null;
}

export interface CallActivityRecorderOptions {
  roomId: string;
  /** The LiveKit token now in use (re-minted on a rejoin). */
  token: string;
  /** What the user asked to join with; `<LiveKitRoom>` publishes this on connect. */
  mediaIntent: () => { mic: boolean; camera: boolean };
}

export function useCallActivityRecorder(room: Room, { roomId, token, mediaIntent }: CallActivityRecorderOptions): void {
  const enabled = useCallDiagnosticsStore((s) => s.enabled);
  const tokenRef = useRef(token);
  tokenRef.current = token;
  const intentRef = useRef(mediaIntent);
  intentRef.current = mediaIntent;

  // Keep the session's token current without re-attaching the Room.
  useEffect(() => {
    if (session && session.roomId === roomId) session.token = token;
  }, [roomId, token]);

  useEffect(() => {
    if (!enabled) return;
    const s = acquire(roomId, tokenRef.current);
    s.sid = () => room.localParticipant?.sid || null;
    const record = s.record;
    let stopped = false;

    // ── Joined, mic and camera ─────────────────────────────────────────────
    let media: { mic: boolean; camera: boolean } | null = null;
    const actualMedia = () => {
      const lp = room.localParticipant;
      const on = (source: Track.Source) => {
        const pub = lp.getTrackPublication(source);
        return !!pub && !pub.isMuted;
      };
      return { mic: on(Track.Source.Microphone), camera: on(Track.Source.Camera) };
    };
    const onConnected = () => {
      media = intentRef.current();
      record("joined", { mic: media.mic, camera: media.camera });
      factsSentFor = null;
      void samplePath();
    };
    // Like LocalMediaStateSync: only while connected (a reconnect
    // unpublishes and republishes by itself), and unpublish is never a choice.
    const onMedia = () => {
      if (room.state !== ConnectionState.Connected || !media) return;
      const now = actualMedia();
      if (now.mic !== media.mic) record("mic_changed", { on: now.mic });
      if (now.camera !== media.camera) record("camera_changed", { on: now.camera });
      media = now;
    };
    const onState = (state: ConnectionState) => record("connection_state", { state });
    const onDeviceError = (err: unknown) => record("error", errorData("media_devices", err));

    room.on(RoomEvent.Connected, onConnected);
    room.on(RoomEvent.ConnectionStateChanged, onState);
    room.on(RoomEvent.LocalTrackPublished, onMedia);
    room.on(RoomEvent.TrackMuted, onMedia);
    room.on(RoomEvent.TrackUnmuted, onMedia);
    room.on(RoomEvent.MediaDevicesError, onDeviceError);
    // Turned on mid-call (or remounted connected): start from what is on now.
    if (room.state === ConnectionState.Connected) {
      media = actualMedia();
      record("joined", { mic: media.mic, camera: media.camera });
    }

    // ── Levels, once a second ──────────────────────────────────────────────
    let levelsBusy = false;
    let inboundLevelSeen = false;
    const sampleLevels = async () => {
      if (levelsBusy || room.state !== ConnectionState.Connected) return;
      levelsBusy = true;
      try {
        const mic = useVoicePathStore.getState().level;
        const levels = await Promise.all(
          Array.from(room.remoteParticipants.values()).map((p) => {
            const pub = p.getTrackPublication(Track.Source.Microphone);
            const track = pub?.track as RemoteTrack | undefined;
            if (!track || pub?.isMuted) return Promise.resolve(null);
            return track.getRTCStatsReport().then(inboundAudioLevel, () => null);
          }),
        );
        if (stopped) return;
        const known = levels.filter((l): l is number => l !== null);
        if (known.length) inboundLevelSeen = true;
        const received = known.length ? Math.max(...known) : null;
        if (mic === null && received === null) return;
        record("levels", { mic: round(mic), received: round(received) });
      } finally {
        levelsBusy = false;
      }
    };

    // ── Path, network, facts, capabilities, every few seconds ──────────────
    let lastPath: string | null = null;
    let lastNet: { type: string; ip: string; local: string } | null = null;
    let typeChanged = false;
    let factsSentFor: string | null = null;
    let capabilitiesSent = false;
    let pathBusy = false;
    const statsReport = async (): Promise<RTCStatsReport | undefined> => {
      const lp = room.localParticipant;
      for (const source of [Track.Source.Microphone, Track.Source.Camera]) {
        const track = lp.getTrackPublication(source)?.track as LocalTrack | undefined;
        if (track) return track.getRTCStatsReport().catch(() => undefined);
      }
      for (const p of room.remoteParticipants.values()) {
        for (const pub of p.trackPublications.values()) {
          const track = pub.track as RemoteTrack | undefined;
          if (track) return track.getRTCStatsReport().catch(() => undefined);
        }
      }
      return undefined;
    };
    const samplePath = async () => {
      if (pathBusy || room.state !== ConnectionState.Connected) return;
      pathBusy = true;
      try {
        const report = await statsReport();
        if (stopped || !report) return;
        const facts = pathFactsFrom(report);
        const type = networkType();
        const sid = s.sid();
        if (sid && factsSentFor !== sid) {
          factsSentFor = sid;
          record("session_facts", {
            ...browserFacts(describeBrowser(navigator.userAgent)),
            network_type: type,
            public_ip: facts?.publicIp ?? "unknown",
          });
        }
        if (!capabilitiesSent) {
          capabilitiesSent = true;
          record("capabilities", {
            remote_inbound: hasRemoteInbound(report),
            network_type: type !== "unknown",
            navigation_api: "navigation" in window,
            srflx: hasSrflx(report),
            inbound_audio_level: inboundLevelSeen,
          });
        }
        if (facts) {
          const key = pathKey(facts);
          if (key !== lastPath) {
            lastPath = key;
            record("path_changed", pathData(facts));
          }
          const net = { type, ip: facts.publicIp, local: facts.localAddress };
          if (lastNet) {
            const addressMoved = net.ip !== lastNet.ip || (net.local !== "" && net.local !== lastNet.local);
            if (typeChanged || net.type !== lastNet.type) {
              record("network_changed", { network_type: net.type, public_ip: net.ip, inferred: false });
            } else if (addressMoved) {
              record("network_changed", { network_type: net.type, public_ip: net.ip, inferred: true });
            }
          }
          typeChanged = false;
          lastNet = net;
        }
      } finally {
        pathBusy = false;
      }
    };
    const connection = (navigator as Navigator & { connection?: EventTarget }).connection;
    const onConnectionChange = () => {
      typeChanged = true;
      void samplePath();
    };
    connection?.addEventListener?.("change", onConnectionChange);

    const levelsTimer = setInterval(() => void sampleLevels(), LEVELS_MS);
    const pathTimer = setInterval(() => void samplePath(), PATH_MS);
    void samplePath();

    return () => {
      stopped = true;
      clearInterval(levelsTimer);
      clearInterval(pathTimer);
      connection?.removeEventListener?.("change", onConnectionChange);
      room.off(RoomEvent.Connected, onConnected);
      room.off(RoomEvent.ConnectionStateChanged, onState);
      room.off(RoomEvent.LocalTrackPublished, onMedia);
      room.off(RoomEvent.TrackMuted, onMedia);
      room.off(RoomEvent.TrackUnmuted, onMedia);
      room.off(RoomEvent.MediaDevicesError, onDeviceError);
      // Keep the last participation's sid for events after the Room is gone.
      const lastSid = room.localParticipant?.sid || null;
      s.sid = () => lastSid;
      release(s);
    };
  }, [room, roomId, enabled]);
}

const round = (v: number | null) => (v === null ? null : Math.round(Math.min(1, Math.max(0, v)) * 1000) / 1000);
